/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * QubePro uygulama kabuklarında üst header AI ikonunu merkezi AI embed
 * sohbetine bağlayan ortak layout köprüsü.
 */
import { useCallback, useMemo, useState } from 'react';
import { getApplicationCodeForApi, getGlobalAssistantApiBasePath } from '../../config/app.config';
import { collectScreenContext } from '../../utils/screen-context.utils';
import { getPageInfo } from '../../config/page-registry';
import GlobalVoiceAssistant, { LS_AI_PANEL_OPEN, readAiPanelPref } from './GlobalVoiceAssistant';
import type { TopHeaderToolbarAiAssistantConfig } from './TopHeaderToolbar';

type GlobalVoiceAssistantUser = {
  id?: string | number;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | number | null;
  customerId?: string | number | null;
  tenantName?: string | null;
  companyId?: string | number | null;
  companyName?: string | null;
} | null | undefined;

export type GlobalVoiceAssistantLayoutOptions = {
  user: GlobalVoiceAssistantUser;
  language?: string;
  pathname: string;
  search?: string;
  pageTitle?: string;
  /**
   * Ürün kabukları kendi yerelleştirilmiş etiketlerini verebilir.
   * Eski uydu kabukları için alan opsiyoneldir; toolbar kendi güvenli
   * varsayılan etiketlerini kullanır.
   */
  labels?: Required<NonNullable<TopHeaderToolbarAiAssistantConfig['labels']>>;
};

export function useGlobalVoiceAssistantLayout({
  user,
  language,
  pathname,
  search = '',
  pageTitle = '',
  labels,
}: GlobalVoiceAssistantLayoutOptions) {
  // F5 / sayfa geçişi koruma: panel açık kaydedildiyse yerel depodan geri yükle.
  const restoredOpen = readAiPanelPref(LS_AI_PANEL_OPEN);
  const [activationNonce, setActivationNonce] = useState(restoredOpen ? 1 : 0);
  const [panelExpandKey, setPanelExpandKey] = useState(0);
  const [panelOpen, setPanelOpen] = useState(restoredOpen);

  const aiAssistantConfig = useMemo<TopHeaderToolbarAiAssistantConfig>(() => {
    const currentUser = user ?? null;

    return {
      enabled: true,
      apiBasePath: getGlobalAssistantApiBasePath(),
      embedPath: '/ai/assistant/embed',
      applicationCode: getApplicationCodeForApi(),
      contextProvider: () => ({
        pageContext: {
          path: pathname,
          search,
          title: pageTitle || document.title,
          // "Bu ekranı açıkla" için canlı ekran özeti (PII-güvenli) + kurumsal sayfa bilgisi.
          screenContext: collectScreenContext(),
          pageInfo: getPageInfo(getApplicationCodeForApi(), pathname),
        },
        userContext: currentUser
          ? {
              id: currentUser.id,
              email: currentUser.email,
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              role: currentUser.role,
              customerId: currentUser.customerId,
              language,
            }
          : undefined,
        companyContext: {
          tenantName: currentUser?.tenantName,
          companyId: currentUser?.companyId,
          companyName: currentUser?.companyName,
        },
      }),
      labels,
    };
  }, [labels, language, pageTitle, pathname, search, user]);

  const openAssistant = useCallback(() => {
    setActivationNonce((prev) => prev + 1);
  }, []);

  const closeAssistant = useCallback(() => {
    setActivationNonce(0);
    setPanelOpen(false);
  }, []);

  const expandAssistantPanel = useCallback(() => {
    setPanelExpandKey((prev) => prev + 1);
  }, []);

  const node = (
    <GlobalVoiceAssistant
      activationNonce={activationNonce}
      panelExpandKey={panelExpandKey}
      onPanelOpenChange={setPanelOpen}
      apiBasePath={aiAssistantConfig.apiBasePath}
      applicationCode={aiAssistantConfig.applicationCode}
      language={language}
      companyId={user?.companyId}
      contextProvider={aiAssistantConfig.contextProvider}
    />
  );

  return {
    aiAssistantConfig,
    panelOpen,
    toolbarProps: {
      onGlobalVoiceAssistantOpen: openAssistant,
      onGlobalVoiceAssistantClose: closeAssistant,
      globalVoicePanelOpen: panelOpen,
      onGlobalVoiceAssistantExpandPanel: expandAssistantPanel,
      initialGlobalVoiceAssistantEngaged: activationNonce > 0,
    },
    node,
  };
}
